import React, {useMemo, useState, useEffect, useCallback} from 'react';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {
  getDigitResultV2,
  getDigitResultV2Item,
  getQuickPickResult,
} from '@/common-pages/pick-digit/digit-service';
import {ScrollView} from 'react-native';
import ResultTable, {digitCell} from '@/common-pages/result-v2/result-table';
import ResultTitle from '@/common-pages/result-v2/result-title';
import Spin from '@basicComponents/spin';
import theme from '@style';
import {FadeInView} from '@basicComponents/animations';
import {useFocusEffect} from '@react-navigation/native';
import DigitBall from '@/common-pages/pick-digit/components/digit-ball';

type SelectData = {
  label: string;
  value: number;
  type: 'quick' | 'digit' | 'all';
};

function ResultV2() {
  const [digitTypes, setDigitTypes] = useState<SelectData[]>([]);
  const [selectValue, setSelectValue] = useState<SelectData>();
  const [tableList, setTableList] = useState<getDigitResultV2Item[]>([]);
  const [loading, setLoading] = useState(false);
  const typeList = useMemo(() => {
    const len = digitTypes.length;
    // 分割数组为两个数组
    const list1 = digitTypes.slice(0, len / 2);
    const list2 = digitTypes.slice(len / 2, len);
    return [list1, list2];
  }, [digitTypes]);
  const showTableList = useMemo(() => {
    if (selectValue?.type === 'all') {
      return tableList;
    } else {
      return tableList.filter(item => {
        return item.pickThreePackageVo.pickThreeId === selectValue?.value;
      });
    }
  }, [selectValue, tableList]);
  const getAllDigit = async () => {
    setLoading(true);
    const res = await getDigitResultV2({
      pageNo: 1,
      pageSize: 5,
    });
    // 过滤快速3d
    const filterList = res.data.filter(item => {
      return !item.pickThreePackageVo.cycle;
    });
    const quickList: any[] = [];
    const typeList: any[] = filterList.map(item => {
      const isquick = !!item.pickThreePackageVo.cycle;
      const title = isquick
        ? `Quick 3d-${item.pickThreePackageVo.cycle}min`
        : item.pickThreePackageVo.pickName;
      item.pickThreePackageVo.pickName = title;
      if (isquick) {
        quickList.push(item);
      }
      return {
        label: title,
        value: item.pickThreePackageVo.pickThreeId,
        type: isquick ? 'quick' : 'digit',
      };
    });
    const allTypeList = [
      {label: 'ALL', value: 0, type: 'all' as 'all'},
      ...typeList,
    ];
    const quickres = await Promise.all(
      quickList.map(item =>
        getQuickPickResult({
          pickThreeId: item.pickThreePackageVo.pickThreeId,
          pageNo: 1,
          pageSize: 5,
        }),
      ),
    );
    quickres.forEach((item, i) => {
      quickList[i].pickResultList = item.drawResultList;
    });
    setDigitTypes(allTypeList);
    setSelectValue(allTypeList[0]);
    setTableList(filterList);
    setLoading(false);
  };
  useEffect(() => {
    getAllDigit();
  }, []);
  const fn = useCallback(() => {
    getAllDigit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFocusEffect(fn);

  const handlerSelect = async (data: SelectData) => {
    setLoading(true);
    setSelectValue(data);
    try {
      if (data.type === 'all') {
        await getAllDigit();
      } else if (data.type === 'quick') {
        // 更新
        const quickres = await getQuickPickResult({
          pickThreeId: data.value,
          pageNo: 1,
          pageSize: 5,
        });
        const newList = [...tableList];
        const curr = newList.find(item => {
          return item.pickThreePackageVo.pickThreeId === data.value;
        });
        if (curr) {
          curr.pickResultList = quickres.drawResultList as any;
          curr.totalCount = quickres.totalCount;
        }
        setTableList(newList);
      } else if (data.type === 'digit') {
        // 更新
        const digitRes = await getDigitResultV2({
          pickThreeId: data.value,
          pageNo: 1,
          pageSize: 5,
        });
        const newList = [...tableList];
        const curr = newList.findIndex(item => {
          return item.pickThreePackageVo.pickThreeId === data.value;
        });
        newList[curr] = {
          ...newList[curr],
          ...digitRes.data,
        };
        setTableList(newList);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };
  const renderType = (list: SelectData[], index: number) => {
    return (
      <div
        className={`gap-4 flex flex-wrap w-max content-start ${
          index === 0 ? 'mb-3' : ''
        }`}>
        {list.map(item => {
          const selectClass =
            item.value === selectValue?.value
              ? 'border-[#166BE5] text-[#166BE5]'
              : 'text-t2 border-transparent';
          return (
            <div
              onClick={() => handlerSelect(item)}
              className={`bg-[#F6F7FB] border font-medium text-sm rounded-[2.25rem] px-3 h-8 whitespace-nowrap flex items-center justify-center ${selectClass}`}>
              {item.label}
            </div>
          );
        })}
      </div>
    );
  };
  const columns = [
    {
      title: 'Issue',
      key: 'pickNo',
      width: '40%',
      align: 'left' as const,
    },
    {
      title: 'Time',
      key: 'drawTime',
      align: 'center' as const,
      render(text: string, record: any) {
        if (record.drawTimeStr) {
          return record.drawTimeStr;
        }
        return text;
      },
    },
    {
      title: (
        <div className="flex gap-1 justify-end">
          {['A', 'B', 'C'].map((item, _i) => (
            <DigitBall ballSize={24} digit={item} color={item} key={_i} />
          ))}
        </div>
      ),
      key: 'drawResult',
      align: 'right' as const,
      render: (text: string) => {
        return digitCell(text);
      },
    },
  ];
  return (
    <FadeInView
      style={[theme.flex.col, theme.fill.fill, theme.background.lightGrey]}>
      <DetailNavTitle title="Result" hideServer />
      <Spin loading={loading} style={theme.fill.fill}>
        <ScrollView
          stickyHeaderIndices={[0]}
          style={(theme.flex.flex1, {paddingBottom: 100})}>
          {typeList.length && (
            <div className="overflow-x-auto bg-white py-2 px-3 gap-3">
              {typeList.map((item, i) => {
                return renderType(item, i);
              })}
            </div>
          )}

          {showTableList.map(item => {
            return (
              <div className="mt-2">
                <ResultTitle
                  title={item.pickThreePackageVo.pickName}
                  icon={item.pickThreePackageVo.pickLogo}
                  type={item.pickThreePackageVo.cycle ? 'quick' : 'digit'}
                  id={item.pickThreePackageVo.pickThreeId}
                />
                <div className="px-3">
                  <ResultTable
                    key="pickNo"
                    dataSource={item.pickResultList}
                    columns={columns}
                  />
                </div>
              </div>
            );
          })}
        </ScrollView>
      </Spin>
    </FadeInView>
  );
}

export default ResultV2;
